import { useRef, useState, useMemo } from 'react'
import Form from '@rjsf/core'
import {
  Mtds,
  ModalDialog,
  getSampleRegistrationSchema,
  getSampleRegistrationUiSchema,
  type SubSamplesFormData
} from 'prove-common'
import { RJSFSchema } from '@rjsf/utils'
import { ThemedForm, useSampleForm } from '../../hooks/useSampleForm'
import { ErrorBoundary } from '../error-boundary/ErrorBoundary'
import { SchemaDataEditor } from './SchemaDataEditor'

type SchemaPreviewProps = {
  schema: RJSFSchema | undefined
  isCodeView: boolean
}

export const SchemaPreview = ({ schema, isCodeView }: SchemaPreviewProps) => {
  const {
    formData,
    validator,
    customValidate,
    transformErrors,
    formContext,
    isFetching,
    onChange,
    setFormData
  } = useSampleForm({ schema })
  const [isDialogOpen, setDialogOpen] = useState<boolean>(false)
  const [selectedTabIndex, setSelectedTabIndex] = useState<number>(0)
  const [useLokalitetWidget, setUseLokalitetWidget] = useState<boolean>(false)
  const ref = useRef<Form>(null)

  const labPreviewData = useMemo(() => {
    if (!schema || !formData) return {}
    type IncludeProp = RJSFSchema & { ['x-include-in-requisition']?: boolean }
    const properties = (schema.properties || {}) as Record<string, IncludeProp>
    const source = (formData.payload || {}) as Record<string, unknown>

    const metadata = Object.entries(source).reduce<Record<string, unknown>>(
      (acc, [key, value]) => {
        if (key === 'subSamples' || value === undefined) return acc
        const prop = properties[key]
        if (!prop || prop['x-include-in-requisition']) {
          acc[key] = value
        }
        return acc
      },
      {}
    )

    const subSamples = (
      source.subSamples as SubSamplesFormData | undefined
    )?.map((ss) => ({
      subSampleId: ss.subSampleId,
      payload: ss.payload ?? {}
    }))

    return {
      ...(Object.keys(metadata).length > 0 && { metadata }),
      ...(subSamples && subSamples.length > 0 && { subSamples })
    }
  }, [schema, formData])

  return (
    <>
      <Mtds.Grid>
        <Mtds.Heading as='h4'>Forhåndsvisning</Mtds.Heading>
        <Mtds.Togglegroup
          {...({ focusgroup: 'radiogroup' } as Record<string, string>)}
          aria-label='Type lokalitet'
        >
          <Mtds.Togglegroup.Item
            name='lokalitet-toggle'
            value='produsent'
            checked={!useLokalitetWidget}
            onChange={() => setUseLokalitetWidget(false)}
          >
            Produsent
          </Mtds.Togglegroup.Item>
          <Mtds.Togglegroup.Item
            name='lokalitet-toggle'
            value='lokalitet'
            checked={useLokalitetWidget}
            onChange={() => setUseLokalitetWidget(true)}
          >
            Lokalitet
          </Mtds.Togglegroup.Item>
        </Mtds.Togglegroup>
        <Mtds.Card>
          <ErrorBoundary>
            <ThemedForm
              noHtml5Validate
              schema={getSampleRegistrationSchema(
                schema,
                false,
                useLokalitetWidget
              )}
              uiSchema={getSampleRegistrationUiSchema(
                schema,
                false,
                useLokalitetWidget
              )}
              formContext={formContext}
              formData={formData}
              showErrorList='bottom'
              validator={validator}
              customValidate={customValidate}
              transformErrors={transformErrors}
              disabled={isFetching}
              onSubmit={({ formData }) => {
                if (formData) {
                  setFormData(formData)
                  setDialogOpen(true)
                }
              }}
              onChange={onChange}
              omitExtraData
              experimental_defaultFormStateBehavior={{
                constAsDefaults: 'skipOneOf'
              }}
              ref={ref}
            >
              <Mtds.Flex className='mt-8'>
                <Mtds.Button
                  data-variant='secondary'
                  onClick={() => ref.current?.reset()}
                >
                  Tilbakestill forhåndsvisning
                </Mtds.Button>
                <Mtds.Button
                  disabled={isFetching}
                  data-variant='primary'
                  type='submit'
                >
                  Test innsending av skjema
                </Mtds.Button>
              </Mtds.Flex>
            </ThemedForm>
            <ModalDialog
              id='schema-preview-modal'
              closedby='any'
              open={isDialogOpen}
              onClose={() => setDialogOpen(false)}
            >
              <Mtds.Button
                command='close'
                commandfor='schema-preview-modal'
                aria-label='Lukk'
              />
              <Mtds.Prose>
                <Mtds.Heading>Innsendingsdata (JSON)</Mtds.Heading>
                <Mtds.Tabs>
                  <Mtds.Tabs.List>
                    <Mtds.Tabs.Tab
                      aria-selected={selectedTabIndex === 0}
                      onClick={() => setSelectedTabIndex(0)}
                    >
                      Registrert
                    </Mtds.Tabs.Tab>
                    <Mtds.Tabs.Tab
                      aria-selected={selectedTabIndex === 1}
                      onClick={() => setSelectedTabIndex(1)}
                    >
                      Til lab
                    </Mtds.Tabs.Tab>
                  </Mtds.Tabs.List>
                  <Mtds.Tabs.Panel hidden={selectedTabIndex !== 0}>
                    <pre>{JSON.stringify(formData?.payload, null, 2)}</pre>
                  </Mtds.Tabs.Panel>
                  <Mtds.Tabs.Panel hidden={selectedTabIndex !== 1}>
                    <pre>{JSON.stringify(labPreviewData, null, 2)}</pre>
                  </Mtds.Tabs.Panel>
                </Mtds.Tabs>
              </Mtds.Prose>
            </ModalDialog>
          </ErrorBoundary>
        </Mtds.Card>
      </Mtds.Grid>
      {isCodeView && (
        <>
          <Mtds.Divider />
          <Mtds.Grid>
            <Mtds.Heading as='h4'>Skjemadata</Mtds.Heading>
            <Mtds.Card as={Mtds.Grid}>
              <SchemaDataEditor data={formData} />
            </Mtds.Card>
          </Mtds.Grid>
        </>
      )}
    </>
  )
}
